<template>
    <div class="container">
        <div class="d-flex justify-content-center spinner-border primary-icon spinner-position" v-if="page.isLoading"></div>
        <div class="spinner" v-if="page.isLoading"></div>
        <o-bread-crumb>
            <template v-slot:pageName>
                Member
            </template>
            <template v-slot:pageDetail>
                Tracking Members
            </template>
            <template v-slot:pageDescription>
                <button class="btn-back" @click="goBack">
                    <i class="fas fa-arrow-left"></i> Back to List
                </button>
            </template>
        </o-bread-crumb>
        <div v-if="error">
            <div class="alert alert-danger mt-1" role="alert">
                {{ error }}
            </div>
        </div>
        <div v-if="thisComponentShow" class="tracking-wrapper">
            <!-- Quick Stats Section -->
            <div class="quick-stats-section">
                <div class="stats-header">
                    <h4 class="stats-title"><i class="fas fa-chart-line me-2"></i>Tracking Member Registrations</h4>
                    <p class="stats-subtitle">Monitor member registrations by entity source</p>
                </div>
                <div class="stats-cards">
                    <div class="stat-card stat-total">
                        <div class="stat-icon"><i class="fas fa-users"></i></div>
                        <div class="stat-info">
                            <span class="stat-label">Total Registrations</span>
                            <span class="stat-value">{{ stats.total.toLocaleString() }}</span>
                        </div>
                    </div>
                    <div class="stat-card stat-corporate">
                        <div class="stat-icon"><i class="fas fa-building"></i></div>
                        <div class="stat-info">
                            <span class="stat-label">Corporate Sign-ups</span>
                            <span class="stat-value">{{ stats.corporate.toLocaleString() }}</span>
                        </div>
                    </div>
                    <div class="stat-card stat-branch">
                        <div class="stat-icon"><i class="fas fa-code-branch"></i></div>
                        <div class="stat-info">
                            <span class="stat-label">Branch Sign-ups</span>
                            <span class="stat-value">{{ stats.branch.toLocaleString() }}</span>
                        </div>
                    </div>
                    <div class="stat-card stat-merchant">
                        <div class="stat-icon"><i class="fas fa-store"></i></div>
                        <div class="stat-info">
                            <span class="stat-label">Merchant Sign-ups</span>
                            <span class="stat-value">{{ stats.merchant.toLocaleString() }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Filter Bar -->
            <div class="filter-bar">
                <div class="filter-bar-left">
                    <div class="period-buttons">
                        <button :class="['period-btn', selectedTimeScale === 'day' ? 'active' : '']" @click="selectPeriod('day')">Day</button>
                        <button :class="['period-btn', selectedTimeScale === 'week' ? 'active' : '']" @click="selectPeriod('week')">Week</button>
                        <button :class="['period-btn', selectedTimeScale === 'month' ? 'active' : '']" @click="selectPeriod('month')">Month</button>
                    </div>

                    <div class="filter-dropdown">
                        <div class="date-input-wrapper">
                            <input placeholder="Date Range" ref="datePicker" class="filter-input" />
                            <i class="fas fa-calendar-alt calendar-icon"></i>
                            <Transition name="fade">
                                <div v-if="showDateTip" class="date-selection-tip">
                                    <i class="fas fa-info-circle"></i>
                                    Please select another date to complete the range
                                </div>
                            </Transition>
                        </div>
                    </div>

                    <div class="filter-dropdown">
                        <select class="filter-select" v-model="selectedBranch" @change="handleBranchChange" :disabled="branchDashboard">
                            <option v-if="showBranchPlaceholder" value="" disabled selected>Select Branch</option>
                            <option v-else-if="!showBranchPlaceholder && !branchDashboard" value="">All Branch</option>
                            <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                                {{ branch.name }}
                            </option>
                        </select>
                    </div>

                    <div class="filter-dropdown">
                        <select class="filter-select" v-model="selectedMerchant" @change="handleMerchantChange" :disabled="merchantDashboard">
                            <option v-if="showMerchantPlaceholder" value="" disabled selected>Select Merchant</option>
                            <option v-else value="">All Merchant</option>
                            <option v-for="merchant in merchants" :key="merchant.id" :value="merchant.id">
                                {{ merchant.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="filter-bar-right">
                    <button v-if="!branchDashboard && (selectedBranch || selectedMerchant)" @click="clearFilter" class="btn-filter-clear" title="Clear Filter">
                        <i class="fas fa-times me-1"></i> Clear
                    </button>
                    <button class="btn-filter-export" @click.prevent="download_state" :disabled="isExporting">
                        <i v-if="!isExporting" class="fas fa-file-excel me-1"></i>
                        {{ isExporting ? 'Exporting...' : 'Export Excel' }}
                    </button>
                </div>
            </div>

            <!-- Data Table -->
            <div class="table-container">
                <o-table style="text-align:left;" class="tracking-table">
                    <thead>
                        <tr>
                            <td class="fw-bolder" scope="col">NO</td>
                            <td class="fw-bolder" scope="col">REGISTERED DATE</td>
                            <td class="fw-bolder" scope="col">MEMBER NAME</td>
                            <td class="fw-bolder" scope="col">EMAIL</td>
                            <td class="fw-bolder" scope="col">REGISTRATION SOURCE</td>
                            <td class="fw-bolder" scope="col">CAMPAIGN SOURCE</td>
                            <td class="fw-bolder" scope="col">ENTITY LEVEL</td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in members" :key="index">
                            <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                            <td>{{ item.created_at ? formatDateRow(item.created_at) : '-' }}</td>
                            <td class="fw-semibold">{{ formatFullName(item.member) }}</td>
                            <td class="text-muted">{{ item.member?.user?.email || '-' }}</td>
                            <td>{{ getRegistrationSource(item) }}</td>
                            <td>{{ item.campaign?.name || '-' }}</td>
                            <td>
                                <span :class="['entity-badge', getEntityBadgeClass(item.entity_type)]">
                                    {{ getEntityLabel(item.entity_type) }}
                                </span>
                            </td>
                        </tr>
                        <tr v-if="members.length === 0">
                            <td colspan="7" class="text-center py-4">No member tracking data found</td>
                        </tr>
                    </tbody>
                </o-table>
            </div>

            <!-- Pagination -->
            <div class="container mt-4">
                <o-pagination :page="meta.page" :total="meta.total" @callback="navPage"/>
            </div>
        </div>
    </div>
    <br>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, reactive } from 'vue';
import { OBreadCrumb, OPagination, OTable } from '@/components/OmnihControl';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from '@/composables/useToast';

export default {
    components: {
        OBreadCrumb,
        OPagination,
        OTable,
    },
    setup() {
        const store = useStore();
        const router = useRouter();
        const route = useRoute();
        const { showToast } = useToast();
        const meta = ref({ page: 1, limit: 10, total: 0 }); 

        const page = reactive({
            isLoading: false,
        });

        const members = ref([]);
        const branches = ref([]);
        const merchants = ref([]);
        const dateRange = ref([null, null]);
        const datePicker = ref(null);
        const showDateTip = ref(false);
        const isExporting = ref(false);

        const error = ref('');
        const thisComponentShow = ref(true);
        const isFilterApplied = ref(false); 

        const selectedTimeScale = ref('day');
        const selectedBranch = ref('');
        const selectedMerchant = ref('');
        const showBranchPlaceholder = ref(true);
        const showMerchantPlaceholder = ref(true);
        const branchDashboard = ref(false);

        const stats = ref({
            total: 0,
            corporate: 0,
            branch: 0,
            merchant: 0,
        });

        const merchantDashboard = ref(false);

        let flatpickrInstance = null;

        onMounted(() => {
            flatpickrInstance = flatpickr(datePicker.value, {
                mode: 'range',
                dateFormat: 'j M Y',
                onChange: handleDateRangeChange,
                onValueUpdate: (selectedDates) => {
                    showDateTip.value = selectedDates.length === 1;
                },
                onClose: () => {
                    showDateTip.value = false;
                }
            });

            // Default filter: from route query or today
            const tf = route.query.timeframe;
            const curr = new Date();
            
            if (tf === 'this_week') {
                const day = curr.getDay() || 7; 
                const startOfWeek = new Date(curr);
                startOfWeek.setDate(startOfWeek.getDate() - day + 1);
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(endOfWeek.getDate() + 6);
                
                dateRange.value = [startOfWeek, endOfWeek];
                if (flatpickrInstance) flatpickrInstance.setDate([startOfWeek, endOfWeek]);
                selectedTimeScale.value = '';
                applyFilter();
            } else if (tf === 'this_month') {
                const startOfMonth = new Date(curr.getFullYear(), curr.getMonth(), 1);
                const endOfMonth = new Date(curr.getFullYear(), curr.getMonth() + 1, 0);
                
                dateRange.value = [startOfMonth, endOfMonth];
                if (flatpickrInstance) flatpickrInstance.setDate([startOfMonth, endOfMonth]);
                selectedTimeScale.value = '';
                applyFilter();
            } else if (tf === 'this_year') {
                const startOfYear = new Date(curr.getFullYear(), 0, 1);
                const endOfYear = new Date(curr.getFullYear(), 11, 31);
                dateRange.value = [startOfYear, endOfYear];
                if (flatpickrInstance) flatpickrInstance.setDate([startOfYear, endOfYear]);
                selectedTimeScale.value = '';
                applyFilter();
            } else {
                selectPeriod('day');
            }
        });

        onBeforeUnmount(() => {
            if (flatpickrInstance) {
                flatpickrInstance.destroy();
            }
        });

        function load_state() {
            page.isLoading = true;

            store.dispatch('Auth/getBusinessProfile')
            .then(businessProfile => {
                if (businessProfile.branch_id) {
                    branchDashboard.value = true;
                } else {
                    branchDashboard.value = false;
                }

                const filterData = {
                    corporate_id: businessProfile.corporate_id,
                    branch_id: selectedBranch.value || businessProfile.branch_id,
                    merchant_id: selectedMerchant.value || businessProfile.merchant_id,
                };
                if (dateRange.value[0] && dateRange.value[1]) {
                    filterData.dateRange = `${formatDateRaw(dateRange.value[0])},${formatDateRaw(dateRange.value[1])}`;
                }

                store.dispatch('MemberRegisterTracker/read', {
                    meta: meta.value,
                    filter: filterData,
                    search: {}
                }).then(response => {
                    members.value = response.data_res;
                    meta.value = response.meta;
                    if (response.stats) {
                        stats.value = response.stats;
                    }

                    // Auto-select branch/merchant based on user access level
                    if (businessProfile.merchant_id && response.branches) {
                        // Merchant-level user: show only the parent branch, auto-select branch & merchant
                        const parentBranch = response.branches.find(b =>
                            b.merchant && b.merchant.some(m => m.id === businessProfile.merchant_id)
                        );
                        if (parentBranch) {
                            branches.value = [parentBranch];
                            selectedBranch.value = parentBranch.id;
                            showBranchPlaceholder.value = false;
                            branchDashboard.value = true;
                            if (parentBranch.merchant) {
                                merchants.value = parentBranch.merchant;
                            }
                            selectedMerchant.value = businessProfile.merchant_id;
                            showMerchantPlaceholder.value = false;
                            merchantDashboard.value = true;
                        }
                    } else if (businessProfile.branch_id && response.branches) {
                        // Branch-level user: show only their branch, auto-select it, load merchants
                        branches.value = response.branches.filter(branch => branch.id === businessProfile.branch_id);
                        selectedBranch.value = businessProfile.branch_id;
                        showBranchPlaceholder.value = false;
                        branchDashboard.value = true;
                        const selected = branches.value.find(b => b.id === selectedBranch.value);
                        if (selected?.merchant) {
                            merchants.value = selected.merchant;
                        }
                    } else if (response.branches) {
                        // Corporate-level user: show all branches
                        branches.value = response.branches;
                        branchDashboard.value = false;
                    }
                }).catch(error => {
                    console.error('Failed to load tracking data:', error);
                    showToast({
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to load member registration tracking data.',
                    });
                }).finally(() => {
                    page.isLoading = false;
                });
            }).catch(error => {
                console.error(error);
                page.isLoading = false;
            });
        }

        function navPage(p){
            meta.value.page = p;
            load_state()
        }

        const handleBranchChange = () => {
            if (selectedBranch.value === '') {
                showBranchPlaceholder.value = true;
                merchants.value = [];
                selectedMerchant.value = '';
                showMerchantPlaceholder.value = true;
            } else {
                showBranchPlaceholder.value = false;
                const selected = branches.value.find(b => b.id === selectedBranch.value);
                selectedMerchant.value = '';
                showMerchantPlaceholder.value = true;
                if (selected?.merchant) {
                    merchants.value = selected.merchant;
                }
            }
            applyFilter();
        };

        const handleMerchantChange = () => {
            showMerchantPlaceholder.value = selectedMerchant.value === '';
            applyFilter();
        };

        const selectPeriod = (period) => {
            selectedTimeScale.value = period;

            const today = new Date();
            let startDate = new Date(today);

            switch (period) {
                case 'day':
                    // startDate is already today
                    break;
                case 'week':
                    startDate.setDate(today.getDate() - 7);
                    break;
                case 'month':
                    startDate.setMonth(today.getMonth() - 1);
                    break;
            }

            dateRange.value = [startDate, today];
            if (flatpickrInstance) {
                flatpickrInstance.setDate([startDate, today]);
            }
            applyFilter();
        };

        const handleDateRangeChange = (selectedDates) => {
            dateRange.value = [null, null];
            if (selectedDates.length === 2) {
                dateRange.value = selectedDates;
                selectedTimeScale.value = '';
                applyFilter();
            }
        };

        const formatDateRaw = (date) => {
            const d = new Date(date);
            const month = '' + (d.getMonth() + 1);
            const day = '' + d.getDate();
            const year = d.getFullYear();
            return [year, month.padStart(2, '0'), day.padStart(2, '0')].join('-');
        };

        const formatDateRow = (dateString) => {
            const date = new Date(dateString);
            return `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;
        };

        const formatFullName = (member) => {
            if (!member) return '-';
            const title = member.title ? `${member.title} ` : '';
            const first = member.first_name || '';
            const last = member.last_name || '';
            return `${title}${first} ${last}`.trim() || '-';
        };

        const applyFilter = () => {
            page.isLoading = true;
            isFilterApplied.value = true;
            meta.value.page = 1;
            load_state();            
        };

        const clearFilter = () => {
            isFilterApplied.value = false;
            selectedBranch.value = '';
            selectedMerchant.value = '';
            merchants.value = [];
            showBranchPlaceholder.value = true;
            showMerchantPlaceholder.value = true;
            meta.value.page = 1;
            error.value = '';
            selectPeriod('day');
        };

        async function download_state() {
            if (!members.value || members.value.length === 0) {
                showToast({
                    type: 'warning',
                    title: 'No Data',
                    message: 'There is no tracking data to export.',
                });
                return;
            }

            isExporting.value = true;
            try {
                const businessProfile = await store.dispatch('Auth/getBusinessProfile');
                
                const filterData = {
                    corporate_id: businessProfile.corporate_id,
                    branch_id: selectedBranch.value || businessProfile.branch_id,
                    merchant_id: selectedMerchant.value,
                };
                if (dateRange.value[0] && dateRange.value[1]) {
                    filterData.dateRange = `${formatDateRaw(dateRange.value[0])},${formatDateRaw(dateRange.value[1])}`;
                }

                const response = await store.dispatch('MemberRegisterTracker/exportData', {
                    filter: filterData,
                    search: {}
                });

                const url = window.URL.createObjectURL(new Blob([response]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Member_Registration_Tracking_${new Date().toISOString().split('T')[0]}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                showToast({
                    type: 'success',
                    title: 'Success',
                    message: 'Data successfully exported.',
                });
            } catch (error) {
                console.error('Export failed:', error);
                showToast({
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to export tracking data.',
                });
            } finally {
                isExporting.value = false;
            }
        }

        const getEntityLabel = (type) => {
            if (!type) return 'Unknown';
            if (type.includes('Corporate')) return 'Corporate';
            if (type.includes('Branch')) return 'Branch';
            if (type.includes('Merchant')) return 'Merchant';
            return type;
        };

        const getEntityBadgeClass = (type) => {
            if (!type) return 'badge-secondary';
            if (type.includes('Corporate')) return 'badge-corporate';
            if (type.includes('Branch')) return 'badge-branch';
            if (type.includes('Merchant')) return 'badge-merchant';
            return 'badge-secondary';
        };

        const getRegistrationSource = (item) => {
            const name = item.entity?.name || '-';
            return name;
        };

        function goBack() {
            router.push('/member');
        }

        return {
            page,
            members,
            meta,
            branches,
            merchants,
            isExporting,
            error,
            thisComponentShow,
            isFilterApplied,
            selectedTimeScale,
            selectedBranch,
            selectedMerchant,
            showBranchPlaceholder,
            showMerchantPlaceholder,
            branchDashboard,
            merchantDashboard,
            datePicker,
            showDateTip,
            stats,
            navPage,
            handleBranchChange,
            handleMerchantChange,
            selectPeriod,
            applyFilter,
            clearFilter,
            download_state,
            formatDateRow,
            getEntityLabel,
            getEntityBadgeClass,
            getRegistrationSource,
            formatFullName,
            goBack
        };
    }
}
</script>

<style scoped>
/* Tracking Wrapper */
.tracking-wrapper {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    padding: 24px;
}

/* Quick Stats Section */
.quick-stats-section {
    margin-bottom: 24px;
}

.stats-header {
    margin-bottom: 16px;
}

.stats-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 4px 0;
    display: flex;
    align-items: center;
}

.stats-title i {
    color: var(--primary-color, #F27178);
}

.stats-subtitle {
    font-size: 0.85rem;
    color: #6b7280;
    margin: 0;
}

.stats-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

.stat-card {
    border-radius: 14px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 14px;
    color: #fff;
    min-height: 90px;
}

.stat-total {
    background: var(--primary-gradient, linear-gradient(135deg, #f87171 0%, #ef4444 100%));
}

.stat-corporate {
    background: var(--primary-gradient, linear-gradient(135deg, #f87171 0%, #ef4444 100%));
    filter: brightness(0.95);
}

.stat-branch {
    background: var(--primary-gradient, linear-gradient(135deg, #f87171 0%, #ef4444 100%));
    filter: brightness(0.88);
}

.stat-merchant {
    background: var(--primary-gradient, linear-gradient(135deg, #f87171 0%, #ef4444 100%));
    filter: brightness(0.85);
}

.stat-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
}

.stat-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.stat-label {
    font-size: 12px;
    font-weight: 500;
    opacity: 0.9;
}

.stat-value {
    font-size: 28px;
    font-weight: 800;
    line-height: 1.1;
}

/* Filter Bar */
.filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 20px;
    padding: 14px 16px;
    background: #f8f9fa;
    border-radius: 12px;
    border: 1px solid #e9ecef;
}

.filter-bar-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.filter-bar-right {
    display: flex;
    align-items: center;
    gap: 10px;
}

/* Period Buttons */
.period-buttons {
    display: flex;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
}

.period-btn {
    padding: 8px 20px;
    border: none;
    background: #fff;
    font-size: 13px;
    font-weight: 600;
    color: #4b5563;
    cursor: pointer;
    transition: all 0.2s;
    border-right: 1px solid #d1d5db;
}

.period-btn:last-child {
    border-right: none;
}

.period-btn:hover {
    background: var(--secondary-color, rgba(249, 62, 62, 0.08));
    color: var(--primary-color, #F27178);
}

.period-btn.active {
    background: var(--primary-color, #F27178);
    color: #fff;
}

/* Filter Inputs */
.filter-select,
.filter-input {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 13px;
    height: 38px;
    min-width: 160px;
    background-color: #fff;
    color: #374151;
}

.filter-select:focus,
.filter-input:focus {
    border-color: var(--primary-color, #F27178);
    box-shadow: 0 0 0 2px var(--secondary-color, rgba(249, 62, 62, 0.15));
    outline: none;
}

/* Date Input Wrapper */
.date-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.date-input-wrapper .filter-input {
    padding-right: 34px;
    width: 200px;
}

.calendar-icon {
    position: absolute;
    right: 10px;
    color: var(--primary-color, #F27178);
    pointer-events: none;
    font-size: 13px;
}

/* Action Buttons */
.btn-filter-apply {
    background-color: var(--primary-color, #F27178);
    color: #fff;
    font-weight: 600;
    font-size: 13px;
    padding: 0 22px;
    height: 38px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-filter-apply:hover {
    filter: brightness(0.9);
    color: #fff;
}

.btn-filter-clear {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 38px;
    display: flex;
    align-items: center;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-filter-clear:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

.btn-filter-export {
    background-color: #10b981;
    color: #fff;
    font-weight: 600;
    font-size: 13px;
    padding: 0 22px;
    height: 38px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.btn-filter-export:hover {
    background-color: #059669;
    color: #fff;
}

.btn-filter-export:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Table Container */
.table-container {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.tracking-table {
    white-space: nowrap;
    margin-bottom: 0;
}

.tracking-table thead tr {
    background: var(--secondary-color, rgba(249, 62, 62, 0.08));
}

.tracking-table thead td {
    color: var(--primary-color, #F27178) !important;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 14px 16px !important;
    border-bottom: 2px solid var(--secondary-color, #FEE9E9);
}

.tracking-table tbody td {
    padding: 12px 16px !important;
    vertical-align: middle;
    font-size: 13px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
}

.tracking-table tbody tr:hover {
    background-color: var(--secondary-color, rgba(249, 62, 62, 0.08));
}

.tracking-table tbody tr:last-child td {
    border-bottom: none;
}

/* Entity Badges */
.entity-badge {
    display: inline-block;
    min-width: 90px;
    text-align: center;
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
}

.badge-corporate {
    background: var(--secondary-color, rgba(249, 62, 62, 0.08));
    color: var(--primary-color, #F27178);
    border: 1.5px solid var(--primary-color, #F27178);
}

.badge-branch {
    background: var(--secondary-color, rgba(249, 62, 62, 0.15));
    color: var(--primary-color, #F27178);
    border: 1.5px solid var(--primary-color, #F27178);
}

.badge-merchant {
    background: var(--primary-color, #F27178);
    color: #fff;
    border: 1.5px solid var(--primary-color, #F27178);
}

.badge-secondary {
    background: #f3f4f6;
    color: #6b7280;
    border: 1.5px solid #d1d5db;
}

/* Flatpickr Override */
.flatpickr-input {
    width: 100%;
    font-weight: 600;
    font-size: 13px;
    border: 1px solid #d1d5db;
    height: 38px;
    border-radius: 8px;
    padding: 0.375rem 0.75rem;
    background-clip: padding-box;
    background-color: #fff !important;
}

/* Spinner */
.spinner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    z-index: 200;
}

/* Button Back */
.btn-back {
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-back:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

/* Responsive */
@media (max-width: 1200px) {
    .stats-cards {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 991px) {
    .filter-bar-left {
        flex: 100%;
    }

    .filter-bar-right {
        flex: 100%;
        justify-content: flex-end;
    }

    .filter-dropdown {
        flex: 1;
        min-width: 0;
    }

    .filter-select {
        min-width: 0;
        width: 100%;
    }

    .date-input-wrapper .filter-input {
        width: 100%;
    }
}

@media (max-width: 768px) {
    .tracking-wrapper {
        padding: 16px;
    }

    .stats-cards {
        grid-template-columns: 1fr 1fr;
    }

    .stat-card {
        padding: 14px;
    }

    .stat-value {
        font-size: 22px;
    }

    .filter-bar-left {
        flex-direction: column;
        align-items: stretch;
    }

    .period-buttons {
        width: 100%;
    }

    .period-btn {
        flex: 1;
        text-align: center;
    }

    .filter-bar-right {
        width: 100%;
    }

    .btn-filter-apply,
    .btn-filter-export {
        flex: 1;
        justify-content: center;
    }
}



@media (max-width: 480px) {
    .stats-cards {
        grid-template-columns: 1fr;
    }
}
</style>


